import type { FC, JSX, PropsWithChildren, ReactNode } from "react";
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { CardGrid } from "widgets";

import { getSummaryPageTips } from "@/app-utils/help.js";
import { useURLViewID } from "@/app-utils/hooks/useUrl.js";
import type { InputTableQuerySpec, Organism } from "@/graphql.js";
import type { ProjectColoredWidgetFragment } from "@/modules/projects/graphql/projects-queries.gql.ts";
import { loadView } from "@/redux/modules/saved-view.js";
import type { ITableDataState } from "@/redux/modules/table-data.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.js";
import { ENTITY_TYPE } from "@/settings/settings.js";

import { SummaryButtonHeader } from "../core/navigation-panel/ButtonHeaderHoc.js";
import PageHeader, { type IPageHeaderProps } from "../core/navigation-panel/PageHeader.js";
import styleLayout from "../core/styles/PageLayout.module.scss";
import TipTour from "../core/TipTour.js";
import RefGenomeSelector from "./RefGenomeSelector.js";

interface IGenericEntitySummaryViewOwnProps {
  title: string;
  subtitle?: string;
  titleTrailer?: ReactNode;
  entity: any;
  refGenomes?: Organism[]; // applies only to Variants Summary View
  headerTags?: JSX.Element[];
  project?: ProjectColoredWidgetFragment | null;
  headerAttributes?: JSX.Element[];
  isLoading?: boolean;
  getEntityName?: () => string;
  badge?: IPageHeaderProps["badge"];
  setDocumentTitle?: IPageHeaderProps["setDocumentTitle"];
  tableDataState?: ITableDataState;
  tableQuerySpec?: InputTableQuerySpec;
}

const GenericEntitySummaryView: FC<PropsWithChildren<IGenericEntitySummaryViewOwnProps>> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const viewId = useURLViewID();

  useEffect(() => {
    if (viewId) dispatch(loadView(viewId));
  }, [dispatch, viewId]);

  return (
    <>
      <TipTour tips={getSummaryPageTips()}>
        <PageHeader
          title={props.title}
          subtitle={props.subtitle}
          titleTrailer={props.titleTrailer}
          tipTourId={"summary-page-title"}
          badge={props.badge}
          setDocumentTitle={props.setDocumentTitle}
          toolsWidgets={[
            props.entity?.groupType === ENTITY_TYPE.MUTATION ? (
              <RefGenomeSelector referenceGenomes={props.refGenomes} key={"generic-entity-summary-view-action-1"} />
            ) : null,
          ]}
          actionWidgets={[
            <SummaryButtonHeader
              entity={props.entity}
              getEntityName={props.getEntityName}
              key={"generic-entity-summary-view-action-2"}
              colummnSelectionButton={
                props.tableDataState && props.tableQuerySpec
                  ? {
                      tableData: props.tableDataState,
                      tableQuerySpec: props.tableQuerySpec,
                    }
                  : undefined
              }
            />,
          ]}
          tags={props.headerTags}
          widgets={props.headerAttributes}
          project={props.project}
          isLoading={props.isLoading}
        />
      </TipTour>
      <CardGrid isLoading={props.isLoading} className={styleLayout.page}>
        {props.children}
      </CardGrid>
    </>
  );
};

export default GenericEntitySummaryView;
